/**
 * The banner line under the campaign name on the booking site. One line of up
 * to 80 characters; left blank, the banner describes the discount instead.
 */
import {
  CAMPAIGN_DESCRIPTION_MAX_LENGTH,
  campaignBannerDescription,
  type CampaignDiscount,
} from '@bonsai/shared';

import { fieldBoxStyle } from '../../../shared/date-time-range/picker-styles';
import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { EditorFormSection } from '../../shared/editor-page/EditorFormSection';

import { singleLine } from './campaign-form';

export interface CampaignDescriptionSectionProps {
  value: string;
  /** The discount as entered, or null while the amount is not valid. */
  discount: CampaignDiscount | null;
  error?: string;
  onChange: (description: string) => void;
}

/** An example the placeholder and hint use until there is a real discount to describe. */
const EXAMPLE_FALLBACK = '20% off every ticket';

export function CampaignDescriptionSection(p: CampaignDescriptionSectionProps) {
  const atLimit = p.value.length >= CAMPAIGN_DESCRIPTION_MAX_LENGTH;
  const fallback = p.discount
    ? campaignBannerDescription({ ...p.discount, description: null })
    : EXAMPLE_FALLBACK;
  const hint = p.discount
    ? `Left blank, the banner reads “${fallback}”.`
    : `Left blank, the banner describes the discount, like “${fallback}”.`;
  return (
    <EditorFormSection
      title="Banner description"
      titleId="campaign-description-title"
      hint="The line under the campaign name on the booking app banner."
    >
      <div style={{ position: 'relative' }}>
        <textarea
          aria-labelledby="campaign-description-title"
          aria-describedby="campaign-description-count"
          aria-invalid={Boolean(p.error)}
          rows={2}
          maxLength={CAMPAIGN_DESCRIPTION_MAX_LENGTH}
          value={p.value}
          placeholder={fallback}
          onChange={(e) => p.onChange(singleLine(e.target.value))}
          style={{
            ...fieldBoxStyle(false, Boolean(p.error)),
            width: '100%',
            height: 'auto',
            minHeight: 62,
            padding: '9px 11px 22px',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.4,
            resize: 'none',
            fontFamily: 'inherit',
            display: 'block',
          }}
        />
        <span
          id="campaign-description-count"
          className="nb-tabular"
          style={{
            position: 'absolute',
            right: 10,
            bottom: 6,
            fontSize: 11,
            fontWeight: 600,
            color: atLimit ? 'var(--nb-warn)' : 'var(--nb-faint)',
          }}
        >
          {p.value.length}/{CAMPAIGN_DESCRIPTION_MAX_LENGTH}
        </span>
      </div>
      {!p.value.trim() && <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>{hint}</div>}
      {p.error && <EditorFieldError id="campaign-description-error">{p.error}</EditorFieldError>}
    </EditorFormSection>
  );
}
